// deps
import React, { ReactElement, useContext, useMemo, ReactNode, useCallback, useState, memo, useEffect } from "react";

// internal deps
import { LinkAccountView } from "@swiggy-private/payment-ui";
import { AnalyticsContext, CheckoutRoute, getBridgeSdk, useNavigate, useOnBack } from "@swiggy-private/checkout-core";
import { useEffectOnce, captureError } from "@swiggy-private/core-ui";
import { WALLET_CODES } from "@swiggy-private/payment-types";
import { useMountedRef } from "@swiggy-private/core-ui";

// components
import CommonUIWrapper from "@payment-client/components/common-ui-wrapper/CommonUIWrapper";
import CommonHeaderContent from "@payment-client/components/common-header-content/CommonHeaderContent";
import PaymentModesWrapper from "@payment-client/components/payment-modes/components/payment-modes-wrapper";

// hooks
import { useWalletVerifyOtp } from "@payment-client/hooks/UseWalletVerifyOtp";

// analytics
import {
    linkWalletAccountImpressionEvent,
    linkWalletAccountVerifyClickEvent,
    linkWalletAccountResendClickEvent,
    linkWalletAccountOtpErrorEvent,
    linkAccountSuccessImpressionEvent,
} from "@payment-client/analytics/events/LinkWallet";

// context
import { WalletBalanceContext } from "@payment-client/contexts/WalletBalanceContext";
import { LinkWalletContext } from "@payment-client/contexts/LinkWalletContext";

// helpers
import { getLinkWalletPageTitle } from "@payment-client/helpers/WalletHelper";
import { showNativeAlert } from "@payment-client/utils/Native";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// constants
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";
import { WALLET_DISPLAY_NAMES } from "@payment-client/constants/Wallet";
import { CONTEXT } from "@payment-client/analytics/constants";
import { PAYLATER_TYPES } from "@swiggy-private/payment-types";
import { PaylaterLinkedContext } from "@payment-client/contexts/PaylaterLinkedContext";
import { LINK_ACCOUNT_SOURCE } from "@swiggy-private/native-bridge";

interface PaymentLinkWalletProps
    extends RouteProps<{
        walletCode: string;
        mobileNumber: string;
        paymentCode: string;
        iconUrl: string;
    }> {
    isPaymentModeRoute?: boolean;
}

const VerifyWalletLinkOtpWrapper = function VerifyWalletLinkOtpWrapper(props: {
    children: ReactNode;
    walletCode: WALLET_CODES;
    isPaymentModeRoute: boolean;
}): ReactElement {
    const { children, walletCode, isPaymentModeRoute } = props;
    const title = getLinkWalletPageTitle(walletCode);

    if (isPaymentModeRoute) {
        return <PaymentModesWrapper title={title}>{children}</PaymentModesWrapper>;
    }

    const header = <CommonHeaderContent title={title} />;
    return <CommonUIWrapper header={header} content={children} />;
};

function VerifyWalletLinkOTP({ data, isPaymentModeRoute = false }: PaymentLinkWalletProps): ReactElement | null {
    const isMountedRef = useMountedRef();
    const [showSuccessBS, setShowSuccessBS] = useState<boolean>(false);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { setLinkWalletState } = useContext(LinkWalletContext);
    const navigateTo = useNavigate();

    const mobileNumber = data?.mobileNumber ?? "";
    const walletCode = (data?.walletCode as WALLET_CODES) || "";
    const paymentCode = data?.paymentCode ?? "";
    const iconUrl = data?.iconUrl ?? "";

    const { refetch: refetchBalance } = useContext(WalletBalanceContext);
    const { refetchPaylaterLinkStatus } = useContext(PaylaterLinkedContext);
    const {
        verifyOtp,
        resendInProgress,
        resendOtp,
        verifyInProgress,
        sendLinkAccountStatusToNative,
    } = useWalletVerifyOtp({ walletCode, mobileNumber, paymentCode, isPaymentModeRoute });

    const linkWalletPageSource = isPaymentModeRoute
        ? CONTEXT.LINK_WALLET_SOURCE_ACCOUNT
        : CONTEXT.LINK_WALLET_SOURCE_PAYMENT;

    const onBack = useOnBack();

    const setStopNavigationInRouteData = useCallback(
        (stopNavigation: boolean) => {
            navigateTo(
                isPaymentModeRoute
                    ? CheckoutRoute.PAYMENT_MODES_VERIFY_WALLET_LINK_OTP
                    : CheckoutRoute.VERIFY_WALLET_LINK_OTP,
                { ...data, stopNavigation },
            );
        },
        [data, isPaymentModeRoute, navigateTo],
    );

    const refetchAndBack = useCallback(() => {
        setStopNavigationInRouteData(false);
        refetchBalance(walletCode);

        if (walletCode === WALLET_CODES.paytm) {
            refetchPaylaterLinkStatus(PAYLATER_TYPES.PAYTM_POSTPAID);
        }
        //Todo onBack(2) is written instead of navigateTo as when the user navigate to a route
        //which was already visited then the routeStack gets updated but history stack is not updated
        //so the history and route stack won't be in sync and when history.go(-1) is done error would occur.
        //Solutioning would have to be done on how to keep route stack and history stack in sync
        setTimeout(() => {
            onBack(2);
        }, 100);
    }, [onBack, refetchBalance, refetchPaylaterLinkStatus, setStopNavigationInRouteData, walletCode]);

    const onCloseBs = useCallback(() => {
        setShowSuccessBS(false);
        if (typeof sendLinkAccountStatusToNative === "function") {
            sendLinkAccountStatusToNative();
            getBridgeSdk()
                .sendLinkAccountStatusToNative({
                    paymentCode: PAYLATER_TYPES.PAYTM_POSTPAID,
                    status: "SUCCESS",
                    source: isPaymentModeRoute ? LINK_ACCOUNT_SOURCE.ACCOUNTS : LINK_ACCOUNT_SOURCE.PAYMENTS,
                    linkedMobileNumber: mobileNumber,
                })
                .catch(err => {
                    /* istanbul ignore next */
                    __DEV__ && console.error(err);
                });
        }
        if (!isPaymentModeRoute) {
            refetchAndBack();
        }
    }, [isPaymentModeRoute, mobileNumber, refetchAndBack, sendLinkAccountStatusToNative]);

    useEffect(() => {
        if (showSuccessBS) {
            linkAccountSuccessImpressionEvent(walletCode, linkWalletPageSource, impressionEvent);
        }
    }, [impressionEvent, linkWalletPageSource, showSuccessBS, walletCode]);

    const _verifyOtp = useCallback(
        async (otpCode: string) => {
            if (verifyInProgress) {
                return;
            }

            linkWalletAccountVerifyClickEvent(walletCode, linkWalletPageSource, clickEvent);

            try {
                const response = await verifyOtp(otpCode);
                if (isMountedRef.current) {
                    if (response.isOk()) {
                        setLinkWalletState({ mobileNumber: "" });
                        setStopNavigationInRouteData(true);
                        setShowSuccessBS(true);
                    } else {
                        const message = response.unwrapErr().statusMessage;
                        linkWalletAccountOtpErrorEvent(walletCode, linkWalletPageSource, message, impressionEvent);
                        showNativeAlert({ message });
                    }
                }
            } catch (err) {
                captureError(err);
                isMountedRef.current && showNativeAlert({ message: OOPS_SOMETHING_WENT_WRONG });
            }
        },
        [
            clickEvent,
            impressionEvent,
            isMountedRef,
            linkWalletPageSource,
            setLinkWalletState,
            setStopNavigationInRouteData,
            verifyInProgress,
            verifyOtp,
            walletCode,
        ],
    );

    const _resendOtp = useCallback(() => {
        linkWalletAccountResendClickEvent(walletCode, linkWalletPageSource, clickEvent);
        resendOtp();
    }, [walletCode, linkWalletPageSource, clickEvent, resendOtp]);

    const otpActions = useMemo(
        () => ({ verifyInProgress, resendInProgress, verifyOtp: _verifyOtp, resendOtp: _resendOtp }),
        [_resendOtp, _verifyOtp, resendInProgress, verifyInProgress],
    );

    useEffectOnce(() => {
        linkWalletAccountImpressionEvent(walletCode, linkWalletPageSource, impressionEvent);
    });

    return (
        <VerifyWalletLinkOtpWrapper walletCode={walletCode} isPaymentModeRoute={isPaymentModeRoute}>
            <LinkAccountView
                actions={otpActions}
                mobile={mobileNumber}
                showSuccessBS={showSuccessBS}
                onCloseBS={onCloseBs}
                iconUrl={iconUrl}
                walletCode={walletCode.toUpperCase()}
                displayName={WALLET_DISPLAY_NAMES[walletCode]}
            />
        </VerifyWalletLinkOtpWrapper>
    );
}

export default memo(VerifyWalletLinkOTP);
